// ErrorBoundary.jsx
//
// Top-level safety net. Catches uncaught React render errors and shows
// a recovery screen instead of leaving the user staring at a blank
// white page.
//
// Beyond the basic class-component pattern this adds three things that
// matter on a launch day where we don't have Sentry hooked up:
//
//   1. Persists the error to localStorage so the user can read it back
//      to support even after they reload. (Reload is the only thing
//      most non-technical users will think to try.)
//
//   2. Offers TWO recovery paths — plain reload, and a "Sign out &
//      reload" that wipes localStorage and sessionStorage. The second
//      one handles the class of bugs that come from corrupted client
//      state (stale auth tokens, half-completed onboarding flags, the
//      service-worker reload-loop survivors).
//
//   3. A "Copy details" button that puts the error message + stack +
//      app version + user-agent on the clipboard so a stuck user can
//      paste it into WhatsApp / email without typing anything out.
//
// Window-level handlers for async errors (unhandled promise rejections,
// errors thrown inside event handlers that React boundaries miss)
// install themselves on mount and persist the same payload.

import React from 'react';
import { RefreshCw, AlertTriangle, LogOut, Copy, Check, MessageCircle } from 'lucide-react';
import { logError, formatErrorReport, getSessionId } from '../lib/errorLog';

const SUPPORT_EMAIL = 'support@docketflowapp.com';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, copied: false };
    this.onWindowError    = this.onWindowError.bind(this);
    this.onUnhandledReject = this.onUnhandledReject.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logError(error, { source: 'react-boundary', componentStack: info?.componentStack?.slice(0, 500) });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error:', error, info);
  }

  componentDidMount() {
    // Window-level handlers — capture async errors (promise
    // rejections, errors inside event handlers) that React's class
    // boundary doesn't see. These don't crash the UI, but they DO
    // log a payload so we have a paper trail when the user reports
    // "the save didn't work".
    window.addEventListener('error',              this.onWindowError);
    window.addEventListener('unhandledrejection', this.onUnhandledReject);
  }

  componentWillUnmount() {
    window.removeEventListener('error',              this.onWindowError);
    window.removeEventListener('unhandledrejection', this.onUnhandledReject);
  }

  onWindowError(e) {
    // Filter out ResizeObserver loop spam and the like; only
    // capture real errors.
    if (!e?.error || /ResizeObserver loop/i.test(e.message || '')) return;
    logError(e.error, { source: 'window-error', filename: e.filename, lineno: e.lineno });
  }

  onUnhandledReject(e) {
    logError(e?.reason || e, { source: 'unhandled-rejection' });
  }

  // Hard recovery — wipe client state in case it's corrupted.
  hardReset = () => {
    try { localStorage.clear(); }   catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    window.location.href = window.location.origin;
  };

  copyDetails = async () => {
    // Use the full structured report from the log buffer — includes
    // any earlier errors that may have led up to this one, not just
    // the single render error that crashed the tree.
    const text = formatErrorReport();
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Older browsers — fall back to a manual prompt the user can
      // long-press to copy from.
      // eslint-disable-next-line no-alert
      window.prompt('Copy this and send to support:', text);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || 'Unknown error';
    const sessionId = getSessionId();
    const supportSubject = encodeURIComponent('DocketFlow error — ' + msg.slice(0, 80));
    const supportBody    = encodeURIComponent(
      `I hit an error in DocketFlow.\n\nSession: ${sessionId}\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}\nMessage: ${msg}\n`
    );

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 sm:p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
            <AlertTriangle size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Something went wrong
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            The app hit an unexpected error. Try reloading first &mdash; that fixes
            most things. If it keeps happening, sign out below to clear your
            cached session.
          </p>
          {msg && (
            <p className="text-xs font-mono bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg px-4 py-2 text-left break-all max-h-28 overflow-auto">
              {msg}
            </p>
          )}

          {/* Primary recovery: reload */}
          <button
            onClick={() => window.location.reload()}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition"
          >
            <RefreshCw size={15} /> Reload page
          </button>

          {/* Secondary recovery: hard reset */}
          <button
            onClick={this.hardReset}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-xl border border-slate-200 dark:border-slate-600 transition"
          >
            <LogOut size={15} /> Sign out &amp; reload
          </button>

          {/* Diagnostics */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={this.copyDetails}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            >
              {this.state.copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
              {this.state.copied ? 'Copied' : 'Copy details'}
            </button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${supportSubject}&body=${supportBody}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            >
              <MessageCircle size={13} /> Email support
            </a>
          </div>
          {/* Session id — readable so a user can dictate it over the
              phone to support, who can then pull the matching server-
              side logs. */}
          <p className="text-[10px] uppercase tracking-widest text-slate-400 pt-1">
            Reference: <span className="font-mono font-bold text-slate-500">{sessionId}</span>
          </p>
        </div>
      </div>
    );
  }
}
